// Admin console: the identities the platform has actually seen acting, per
// customer, derived from the audit trail.
//
// Honest scope, stated in the UI too: user accounts live in Keycloak, this
// service keeps no directory. So this answers "who touched this tenant and
// when", not "which accounts exist". Dormant accounts do not appear.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { adminApi } from '../api/adminClient';
import type { AdminUser } from '../api/types';
import { usePaginated } from '../lib/hooks';
import { useLang } from '../lib/useLang';
import { formatDateTime, formatNumber } from '../lib/format';
import { PageHeader } from '../components/PageHeader';
import { ApiState, EmptyState } from '../components/States';
import { LoadMore } from '../components/Pagination';
import { Badge, Card } from '../components/ui';
import { IconSearch, IconUsers } from '../components/Icons';

const PAGE = 30;

export function AdminUsers() {
  const { t } = useTranslation();
  const lang = useLang();
  const [q, setQ] = useState('');
  const list = usePaginated((cursor) => adminApi.adminUsers(PAGE, cursor, q || undefined), [q]);

  return (
    <>
      <PageHeader title={t('admin.users.title')} subtitle={t('admin.users.subtitle')} />

      <Card>
        <p className="muted" style={{ padding: '12px 16px 0', margin: 0, fontSize: 13 }}>
          {t('admin.users.sourceNote')}
        </p>
        <div className="row" style={{ padding: '12px 16px' }}>
          <span className="input" style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
            <IconSearch width={16} height={16} />
            <input
              style={{ border: 'none', outline: 'none', flex: 1, background: 'transparent' }}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('admin.users.filterPlaceholder')}
              aria-label={t('admin.users.filterPlaceholder')}
            />
          </span>
        </div>

        <ApiState
          status={list.status}
          error={list.error}
          data={list.status === 'error' ? null : list.items}
          onRetry={list.reload}
        >
          {(items: AdminUser[]) =>
            items.length === 0 ? (
              <EmptyState title={t('admin.users.empty')} icon={<IconUsers width={26} height={26} />} />
            ) : (
              <>
                <div className="table-wrap">
                  <table className="data">
                    <thead>
                      <tr>
                        <th>{t('admin.users.colUser')}</th>
                        <th>{t('admin.buckets.colCustomer')}</th>
                        <th className="num">{t('admin.users.colActions')}</th>
                        <th>{t('admin.users.colLastAction')}</th>
                        <th>{t('admin.users.colLastSeen')}</th>
                        <th>{t('admin.users.colFirstSeen')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((u) => (
                        <tr key={`${u.tenantId}:${u.actor}`}>
                          <td className="cell-strong">{u.actor}</td>
                          <td>
                            {u.tenantId ? (
                              <Link to={`/customers/${encodeURIComponent(u.tenantId)}`}>{u.tenantName}</Link>
                            ) : (
                              <span className="muted">{t('admin.users.platformScope')}</span>
                            )}
                          </td>
                          <td className="num">{formatNumber(u.actions, lang)}</td>
                          <td>
                            <Badge>{u.lastAction || '—'}</Badge>
                          </td>
                          <td className="muted">{formatDateTime(u.lastSeen, lang)}</td>
                          <td className="muted">{formatDateTime(u.firstSeen, lang)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <LoadMore hasMore={list.hasMore} loading={list.loadingMore} onLoadMore={list.loadMore} />
              </>
            )
          }
        </ApiState>
      </Card>
    </>
  );
}
